import React, { useState } from 'react';
import { Button, Card, Form, Row, Col, InputGroup, Collapse, Badge } from 'react-bootstrap';
import './FilterComponent.css';

export interface FilterOption {
    value: string;
    label: string;
}

export interface AppliedFilter {
    type: string;
    value: string;
    label: string;
    displayValue?: string; // Store the display value to avoid context issues
}

export interface DropdownOption {
    value: string;
    label: string;
}

interface FilterComponentProps {
    showFilter: boolean;
    filterText: string;
    filterType: string;
    filterOptions: FilterOption[];
    appliedFilters: AppliedFilter[];
    dropdownOptions?: DropdownOption[];
    onFilterTypeChange: (type: string) => void;
    onFilterTextChange: (text: string) => void;
    onFilterTextClear: () => void;
    onFilterToggle: () => void;
    onFilterApply: (text: string, type: string) => void;
    onFilterRemove: (type: string) => void;
    placeholderText?: string;
}

const FilterComponent: React.FC<FilterComponentProps> = ({
    showFilter,
    filterText,
    filterType,
    filterOptions,
    appliedFilters,
    dropdownOptions,
    onFilterTypeChange,
    onFilterTextChange,
    onFilterTextClear,
    onFilterToggle,
    onFilterApply,
    onFilterRemove,
    placeholderText
}) => {
    const [localFilterText, setLocalFilterText] = useState(filterText);

    // Update local state when prop changes
    React.useEffect(() => {
        setLocalFilterText(filterText);
    }, [filterText]);

    const handleFilterApply = () => {
        onFilterApply(localFilterText, filterType);
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleFilterApply();
        }
    };

    const handleFilterTextClear = () => {
        setLocalFilterText('');
        onFilterTextClear();
    };

    return (
        <div className="filter-component">
            {/* Applied Filters Display */}
            {appliedFilters.length > 0 && (
                <div className="applied-filters mb-3">
                    <div className="d-flex flex-wrap gap-2 align-items-center">
                        <span className="text-muted small">Filtros aplicados:</span>
                        {appliedFilters.map((filter, index) => {
                            // Always use stored display value if available, otherwise use the value itself
                            const displayValue = filter.displayValue || filter.value;
                            return (
                                <Badge
                                    key={`${filter.type}-${index}`}
                                    bg="primary"
                                    className="applied-filter-badge d-flex align-items-center gap-1"
                                >
                                    <span className="filter-label">{filter.label}:</span>
                                    <span className="filter-value">{displayValue}</span>
                                    <Button
                                        variant="link"
                                        size="sm"
                                        className="remove-filter-btn p-0 text-white"
                                        onClick={() => onFilterRemove(filter.type)}
                                        title={`Remover filtro ${filter.label}`}
                                    >
                                        ✕
                                    </Button>
                                </Badge>
                            );
                        })}
                    </div>
                </div>
            )}

            <Button
                variant="outline-secondary"
                onClick={onFilterToggle}
                className="filter-toggle-button d-inline-flex align-items-center gap-2 mb-2"
                aria-expanded={showFilter}
            >
                <span>{showFilter ? '✕' : '🔍'}</span>
                {showFilter ? 'Fechar Filtro' : 'Filtrar'}
            </Button>
            <Collapse in={showFilter}>
                <div className="filter-container">
                    <Card className="filter-card">
                        <Card.Body>
                            <Row className="align-items-end g-3">
                                <Col lg={3} md={4}>
                                    <Form.Group>
                                        <Form.Label>Filtrar por</Form.Label>
                                        <Form.Control
                                            as="select"
                                            value={filterType}
                                            onChange={(e) => onFilterTypeChange(e.target.value)}
                                            className="filter-select"
                                        >
                                            {filterOptions.map(option => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </Form.Control>
                                    </Form.Group>
                                </Col>
                                <Col lg={6} md={5}>
                                    <Form.Group>
                                        <Form.Label>Buscar</Form.Label>
                                        {dropdownOptions && dropdownOptions.length > 0 ? (
                                            <Form.Control
                                                as="select"
                                                value={localFilterText}
                                                onChange={(e) => setLocalFilterText(e.target.value)}
                                                className="search-input"
                                            >
                                                <option value="">Selecione...</option>
                                                {dropdownOptions.map(option => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </Form.Control>
                                        ) : (
                                            <InputGroup>
                                                <Form.Control
                                                    type="text"
                                                    placeholder={placeholderText || 'Buscar...'}
                                                    value={localFilterText}
                                                    onChange={(e) => setLocalFilterText(e.target.value)}
                                                    onKeyPress={handleKeyPress}
                                                    className="search-input"
                                                />
                                                <Button
                                                    variant="outline-secondary"
                                                    onClick={handleFilterTextClear}
                                                    className="clear-button"
                                                >
                                                    ✕
                                                </Button>
                                            </InputGroup>
                                        )}
                                    </Form.Group>
                                </Col>
                                <Col lg={3} md={3}>
                                    <Form.Group>
                                        <Form.Label>&nbsp;</Form.Label>
                                        <Button
                                            variant="primary"
                                            onClick={handleFilterApply}
                                            className="w-100"
                                        >
                                            Buscar
                                        </Button>
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </div>
            </Collapse>
        </div>
    );
};

export default FilterComponent; 